import { useMemo } from "react";

import { Theme } from "@mui/material/styles";
import { FormBackgroundTheme } from "@simplyk/common";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";

export interface ThemedBackgroundShapeConfiguration {
  id: string;
  style: object;
  rotationMultiplier: number;
  scaleMultiplier: number;
  initialTransform?: string;
}

export const useBackgroundShapesConfiguration = (theme?: FormBackgroundTheme | null) => {
  const { isSmallScreen, value: isExtraLargeScreen } = useMediaQuery((t: Theme) => t.breakpoints.up("xl"));

  return useMemo<ThemedBackgroundShapeConfiguration[]>(() => {
    if (isSmallScreen) {
      return getMobileConfig(theme) || getAbstractMobileConfig();
    }

    return getDesktopConfig(theme) || getAbstractDesktopConfig(isExtraLargeScreen);
  }, [isSmallScreen, isExtraLargeScreen, theme]);
};

function getDesktopConfig(theme?: FormBackgroundTheme | null): ThemedBackgroundShapeConfiguration[] | null {
  switch (theme) {
    case FormBackgroundTheme.Animal:
      return getAnimalDesktopConfig();
    case FormBackgroundTheme.Education:
      return getEducationDesktopConfig();
    case FormBackgroundTheme.CultureAndArts:
      return getCultureAndArtsDesktopConfig();
    case FormBackgroundTheme.Religion:
      return getReligionDesktopConfig();
    case FormBackgroundTheme.Environment:
      return getEnvironmentDesktopConfig();
    case FormBackgroundTheme.Golf:
      return getGolfDesktopConfig();
    case FormBackgroundTheme.Health:
      return getHealthDesktopConfig();
    case FormBackgroundTheme.Humanitarian:
      return getHumanitarianDesktopConfig();
    default:
      return null;
  }
}

function getMobileConfig(theme?: FormBackgroundTheme | null): ThemedBackgroundShapeConfiguration[] | null {
  switch (theme) {
    case FormBackgroundTheme.Animal:
      return getAnimalMobileConfig();
    case FormBackgroundTheme.Education:
      return getEducationMobileConfig();
    case FormBackgroundTheme.CultureAndArts:
      return getCultureAndArtsMobileConfig();
    case FormBackgroundTheme.Religion:
      return getReligionMobileConfig();
    case FormBackgroundTheme.Environment:
      return getEnvironmentMobileConfig();
    case FormBackgroundTheme.Golf:
      return getGolfMobileConfig();
    case FormBackgroundTheme.Health:
      return getHealthMobileConfig();
    case FormBackgroundTheme.Humanitarian:
      return getHumanitarianMobileConfig();
    default:
      return null;
  }
}

function getAbstractMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "mobileShape1",
      style: { top: "-64px", left: "-40px", width: "193px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.3,
    },
    {
      id: "mobileShape2",
      style: { bottom: "-16px", right: "-24px", width: "96px", height: "auto" },
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.5,
    },
    {
      id: "mobileShape3",
      style: { top: "35%", right: "-108px", width: "186px", height: "auto" },
      rotationMultiplier: 1.1,
      scaleMultiplier: 0.8,
    },
    {
      id: "mobileShape4",
      style: { bottom: "64px", left: "-80px", width: "133px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
  ];
}

function getAbstractDesktopConfig(isExtraLargeScreen: boolean): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "desktopShape1",
      style: { top: "-64px", left: "-80px", width: "258px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.1,
    },
    {
      id: "desktopShape2",
      style: { top: "-156px", right: "-72px", width: "340px", height: "auto" },
      rotationMultiplier: 1.05,
      scaleMultiplier: 1.05,
    },
    {
      id: "desktopShape3",
      style: { bottom: "40px", left: "-120px", width: "264px", height: "auto" },
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.1,
    },
    {
      id: "desktopShape4",
      style: {
        bottom: isExtraLargeScreen ? "-113px" : "-123px",
        right: isExtraLargeScreen ? "-83px" : "-100px",
        width: "264px",
        height: "auto",
      },
      rotationMultiplier: 1.0,
      scaleMultiplier: 0.95,
    },
  ];
}

function getAnimalMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "animalMobileDog",
      style: { top: "120px", right: "-15px", width: "99px", height: "auto" },
      initialTransform: "scaleX(-1)",
      rotationMultiplier: 0.8,
      scaleMultiplier: 1.0,
    },
    {
      id: "animalMobilePawLeft",
      style: { top: "283px", left: "-5px", width: "41px", height: "auto" },
      initialTransform: "rotate(-32.07deg)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.1,
    },
    {
      id: "animalMobilePawRight",
      style: { top: "395px", right: "-14px", width: "62px", height: "auto" },
      initialTransform: "rotate(31.08deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.0,
    },
    {
      id: "animalMobileCat",
      style: { bottom: "30px", left: "0px", width: "107px", height: "auto" },
      rotationMultiplier: 0.7,
      scaleMultiplier: 1.0,
    },
  ];
}

function getAnimalDesktopConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "animalDesktopDog",
      style: { top: "39px", right: "21px", width: "171px", height: "auto" },
      initialTransform: "scaleX(-1)",
      rotationMultiplier: 0.8,
      scaleMultiplier: 1.0,
    },
    {
      id: "animalDesktopPawLarge",
      style: { top: "87px", left: "151px", width: "97px", height: "auto" },
      initialTransform: "rotate(31.08deg)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.1,
    },
    {
      id: "animalDesktopPawSmall",
      style: { top: "42.7%", left: "123px", width: "55px", height: "auto" },
      initialTransform: "rotate(-32.07deg)",
      rotationMultiplier: 1.1,
      scaleMultiplier: 1.2,
    },
    {
      id: "animalDesktopCat",
      style: { bottom: "-18px", left: "75px", width: "201px", height: "auto" },
      rotationMultiplier: 0.7,
      scaleMultiplier: 1.0,
    },
    {
      id: "animalDesktopPawMedium",
      style: { bottom: "42px", right: "19px", width: "112px", height: "auto" },
      initialTransform: "rotate(-26.24deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.0,
    },
  ];
}

function getEducationDesktopConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "educationDesktopPencilSmall",
      style: { top: "287px", left: "74px", width: "89px", height: "auto" },
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.05,
    },
    {
      id: "educationDesktopPencilLarge",
      style: { top: "56px", right: "-17px", width: "159px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.1,
    },
    {
      id: "educationDesktopGradcap1",
      style: { top: "145px", left: "123px", width: "110px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.0,
    },
    {
      id: "educationDesktopGradcap2",
      style: { bottom: "10px", right: "40px", width: "101px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 1.05,
      scaleMultiplier: 0.95,
    },
    {
      id: "educationDesktopBook",
      style: { bottom: "-10px", left: "129px", width: "171px", height: "auto" },
      rotationMultiplier: 0.75,
      scaleMultiplier: 1.15,
    },
  ];
}

function getEducationMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "educationMobilePencil",
      style: { top: "150px", right: "-24px", width: "77px", height: "auto" },
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
    {
      id: "educationMobileGradcap1",
      style: { top: "379px", right: "-40px", width: "80px", height: "auto" },
      initialTransform: "scaleX(-1)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "educationMobileGradcap2",
      style: { top: "313px", left: "-17px", width: "50px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.1,
    },
    {
      id: "educationMobileBook",
      style: { bottom: "0px", left: "0px", width: "125px", height: "auto" },
      rotationMultiplier: 0.7,
      scaleMultiplier: 1.0,
    },
  ];
}

function getCultureAndArtsDesktopConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "cultureAndArtsDesktopTheater1",
      style: { top: "338px", left: "80px", width: "93px", height: "auto" },
      initialTransform: "rotate(28deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.0,
    },
    {
      id: "cultureAndArtsDesktopMusicNoteTopRight",
      style: { top: "-10px", right: "50px", width: "120px", height: "auto" },
      initialTransform: "rotate(180deg)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "cultureAndArtsDesktopMusicNoteBottomLeft",
      style: { bottom: "-10px", left: "50px", width: "120px", height: "auto" },
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.1,
    },
    {
      id: "cultureAndArtsDesktopPalette",
      style: { top: "92px", left: "123px", width: "121px", height: "auto" },
      rotationMultiplier: 1.05,
      scaleMultiplier: 0.95,
    },
    {
      id: "cultureAndArtsDesktopTheater2",
      style: { bottom: "-5px", right: "17px", width: "110px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
  ];
}

function getCultureAndArtsMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "cultureAndArtsMobileTheater1",
      style: { top: "367px", right: "-35px", width: "80px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.0,
    },
    {
      id: "cultureAndArtsMobilePalette",
      style: { top: "292px", left: "-9px", width: "64px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "cultureAndArtsMobileMusicNote",
      style: { bottom: "-10px", left: "8px", width: "81px", height: "auto" },
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.1,
    },
    {
      id: "cultureAndArtsMobileTheater2",
      style: { top: "33px", right: "-50px", width: "126px", height: "auto" },
      initialTransform: "rotate(28deg)",
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
  ];
}

function getReligionDesktopConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "religionDesktopDoveSmall",
      style: { top: "326px", left: "10px", width: "65px", height: "auto" },
      initialTransform: "scaleX(-1)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.0,
    },
    {
      id: "religionDesktopDoveLarge",
      style: { top: "65px", right: "16px", width: "103px", height: "auto" },
      rotationMultiplier: 0.8,
      scaleMultiplier: 1.05,
    },
    {
      id: "religionDesktopPray",
      style: { top: "130px", left: "114px", width: "109px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.1,
    },
    {
      id: "religionDesktopCandleSmall",
      style: { bottom: "-10px", right: "35px", width: "118px", height: "auto" },
      rotationMultiplier: 1.05,
      scaleMultiplier: 0.95,
    },
    {
      id: "religionDesktopCandleLarge",
      style: { bottom: "-10px", left: "48px", width: "187px", height: "auto" },
      rotationMultiplier: 0.75,
      scaleMultiplier: 1.15,
    },
  ];
}

function getReligionMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "religionMobileDove",
      style: { top: "150px", right: "-38px", width: "75px", height: "auto" },
      rotationMultiplier: 0.8,
      scaleMultiplier: 1.0,
    },
    {
      id: "religionMobilePray1",
      style: { top: "376px", right: "-22px", width: "94px", height: "auto" },
      initialTransform: "rotate(-18deg)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "religionMobilePray2",
      style: { top: "291px", left: "-12px", width: "44px", height: "auto" },
      initialTransform: "rotate(18deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.1,
    },
    {
      id: "religionMobileCandle",
      style: { bottom: "-10px", left: "-26px", width: "125px", height: "auto" },
      rotationMultiplier: 0.7,
      scaleMultiplier: 1.0,
    },
  ];
}

function getEnvironmentDesktopConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "environmentDesktopGlobe",
      style: { top: "296px", left: "92px", width: "76px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
    {
      id: "environmentDesktopLeaf1",
      style: { top: "96px", left: "140px", width: "75px", height: "auto" },
      initialTransform: "scaleX(-1)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "environmentDesktopLeaf2",
      style: { bottom: "70px", right: "47px", width: "79px", height: "auto" },
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.1,
    },
    {
      id: "environmentDesktopTree1",
      style: { bottom: "-10px", left: "59px", width: "160px", height: "auto" },
      rotationMultiplier: 0.7,
      scaleMultiplier: 1.2,
    },
    {
      id: "environmentDesktopTree2",
      style: { top: "54px", right: "-20px", width: "160px", height: "auto" },
      rotationMultiplier: 1.05,
      scaleMultiplier: 0.9,
    },
  ];
}

function getEnvironmentMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "environmentMobileGlobe",
      style: { top: "154px", right: "-52px", width: "102px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
    {
      id: "environmentMobileLeaf1",
      style: { top: "383px", right: "-17px", width: "79px", height: "auto" },
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.1,
    },
    {
      id: "environmentMobileLeaf2",
      style: { top: "263px", left: "-5px", width: "39px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "environmentMobileTree",
      style: { bottom: "40px", left: "-4px", width: "97px", height: "auto" },
      rotationMultiplier: 0.7,
      scaleMultiplier: 1.0,
    },
  ];
}

function getGolfDesktopConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "golfDesktopGreenLarge",
      style: { top: "122px", left: "112px", width: "109px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.05,
    },
    {
      id: "golfDesktopGreenSmall",
      style: { bottom: "59px", right: "60px", width: "123px", height: "auto" },
      initialTransform: "rotate(-18deg)",
      rotationMultiplier: 1.05,
      scaleMultiplier: 0.95,
    },
    {
      id: "golfDesktopBall",
      style: { bottom: "0px", left: "148px", width: "148px", height: "auto" },
      rotationMultiplier: 0.75,
      scaleMultiplier: 1.1,
    },
    {
      id: "golfDesktopClub1",
      style: { top: "60px", right: "-30px", width: "182px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.0,
    },
    {
      id: "golfDesktopClub2",
      style: { top: "319px", left: "63px", width: "96px", height: "auto" },
      initialTransform: "rotate(26deg)",
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
  ];
}

function getGolfMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "golfMobileClub",
      style: { top: "54px", right: "-49px", width: "113px", height: "auto" },
      initialTransform: "rotate(28deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.0,
    },
    {
      id: "golfMobileGreenLarge",
      style: { top: "396px", right: "-12px", width: "88px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "golfMobileGreenSmall",
      style: { top: "259px", left: "2px", width: "73px", height: "auto" },
      initialTransform: "rotate(28deg)",
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.1,
    },
    {
      id: "golfMobileBall",
      style: { bottom: "0px", left: "-19px", width: "109px", height: "auto" },
      rotationMultiplier: 0.7,
      scaleMultiplier: 1.0,
    },
  ];
}

function getHealthDesktopConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "healthDesktopHeartTopLeft",
      style: { top: "149px", left: "149px", width: "89px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.05,
    },
    {
      id: "healthDesktopHeartBottomRight",
      style: { bottom: "81px", right: "47px", width: "82px", height: "auto" },
      initialTransform: "rotate(28deg)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 0.95,
    },
    {
      id: "healthDesktopMentalHealth",
      style: { bottom: "-10px", left: "231px", width: "129px", height: "auto" },
      initialTransform: "scaleX(-1)",
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.1,
    },
    {
      id: "healthDesktopHealth",
      style: { top: "131px", right: "-2px", width: "95px", height: "auto" },
      initialTransform: "rotate(28deg)",
      rotationMultiplier: 1.05,
      scaleMultiplier: 1.0,
    },
    {
      id: "healthDesktopStethoscope",
      style: { top: "369px", left: "116px", width: "74px", height: "auto" },
      initialTransform: "rotate(28deg)",
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
  ];
}

function getHealthMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "healthMobileHealth",
      style: { top: "356px", right: "-34px", width: "92px", height: "auto" },
      initialTransform: "rotate(28deg)",
      rotationMultiplier: 0.9,
      scaleMultiplier: 1.0,
    },
    {
      id: "healthMobileHeart",
      style: { top: "255px", left: "3px", width: "89px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "healthMobileMentalHealth",
      style: { bottom: "0px", left: "91px", width: "86px", height: "auto" },
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.1,
    },
    {
      id: "healthMobileStethoscope",
      style: { top: "69px", right: "-36px", width: "99px", height: "auto" },
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
  ];
}

function getHumanitarianDesktopConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "humanitarianDesktopAid",
      style: { bottom: "35px", left: "87px", width: "100px", height: "auto" },
      rotationMultiplier: 0.8,
      scaleMultiplier: 1.1,
    },
    {
      id: "humanitarianDesktopGlobe1",
      style: { top: "113px", left: "129px", width: "117px", height: "auto" },
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.0,
    },
    {
      id: "humanitarianDesktopGlobe2",
      style: { bottom: "67px", right: "58px", width: "77px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.05,
    },
    {
      id: "humanitarianDesktopHelpingTopRight",
      style: { top: "104px", right: "0px", width: "88px", height: "auto" },
      initialTransform: "scaleX(-1)",
      rotationMultiplier: 1.05,
      scaleMultiplier: 0.95,
    },
    {
      id: "humanitarianDesktopHelpingBottomLeft",
      style: { top: "349px", left: "14px", width: "90px", height: "auto" },
      initialTransform: "rotate(18deg)",
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.1,
    },
  ];
}

function getHumanitarianMobileConfig(): ThemedBackgroundShapeConfiguration[] {
  return [
    {
      id: "humanitarianMobileGlobe1",
      style: { top: "345px", right: "-15px", width: "59px", height: "auto" },
      rotationMultiplier: 0.95,
      scaleMultiplier: 1.0,
    },
    {
      id: "humanitarianMobileHelping",
      style: { top: "246px", left: "0px", width: "100px", height: "auto" },
      initialTransform: "rotate(-28deg)",
      rotationMultiplier: 1.0,
      scaleMultiplier: 1.05,
    },
    {
      id: "humanitarianMobileAid",
      style: { bottom: "0px", left: "81px", width: "74px", height: "auto" },
      rotationMultiplier: 0.8,
      scaleMultiplier: 1.1,
    },
    {
      id: "humanitarianMobileGlobe2",
      style: { top: "60px", right: "-22px", width: "117px", height: "auto" },
      rotationMultiplier: 0.85,
      scaleMultiplier: 1.0,
    },
  ];
}
